import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  AdClient,
  AdPlacement,
  AdvertisingCampaignView,
  AdvertisingOverviewView,
  AdvertisingSessionView,
} from 'shared';
import { advertisingApi } from '@/api/advertising.api';
import { getDesktopBridge } from '@/desktop';

export const useAdvertisingStore = defineStore('advertising', () => {
  const overview = ref<AdvertisingOverviewView | null>(null);
  const activeSession = ref<AdvertisingSessionView | null>(null);
  const loading = ref(false);
  const completing = ref(false);
  const client = computed<AdClient>(() => (getDesktopBridge() ? 'desktop' : 'web'));
  const campaignFor = (placement: AdPlacement): AdvertisingCampaignView | null =>
    overview.value?.placements[placement]?.[0] ?? null;
  async function refresh(): Promise<void> {
    loading.value = true;
    try {
      overview.value = await advertisingApi.overview(client.value);
    } finally {
      loading.value = false;
    }
  }
  async function start(campaign: AdvertisingCampaignView, placement: AdPlacement): Promise<void> {
    activeSession.value = await advertisingApi.begin(campaign.id, placement, client.value);
  }
  async function complete(): Promise<void> {
    if (!activeSession.value) return;
    completing.value = true;
    try {
      const result = await advertisingApi.complete(activeSession.value.id);
      activeSession.value = result.session;
      window.dispatchEvent(new CustomEvent('advertising-rewarded', { detail: result }));
      await refresh();
    } finally {
      completing.value = false;
    }
  }
  function closeSession(): void {
    activeSession.value = null;
  }
  return {
    overview,
    activeSession,
    loading,
    completing,
    client,
    campaignFor,
    refresh,
    start,
    complete,
    closeSession,
  };
});
